import React, { useEffect, useState } from "react";
import { Phone, User, Calendar, Tag, ArrowRight, Users, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEMO_API_KEY = "test_key_123";

export const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiKey = window.localStorage.getItem("apiKey") || DEMO_API_KEY;

  useEffect(() => {
    const loadLeads = async () => {
      try {
        const res = await fetch(`${API_URL}/api/leads`, {
          headers: { "X-API-Key": apiKey }
        });
        if (res.ok) {
          const data = await res.json();
          setLeads(data || []);
        } else {
          setError("Unable to load leads");
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load leads");
      } finally {
        setLoading(false);
      }
    };
    loadLeads();
  }, [apiKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Leads
          </h1>
          <p className="text-slate-500 mt-1">
            Every caller that turned into an opportunity: new patient, new customer, or callback required.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full">
          <Users className="w-4 h-4 text-purple-600" />
          <span className="text-purple-700 text-sm font-medium">{leads.length} Total</span>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!error && leads.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
            <Users className="w-8 h-8 text-purple-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No leads yet</h3>
          <p className="text-slate-500 text-sm max-w-md mx-auto">
            Once your AI receptionist starts handling calls, qualified leads will appear here automatically.
          </p>
        </div>
      )}

      {leads.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="max-h-[540px] overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-white border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Created
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Name
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Phone
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-slate-400 font-medium">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr
                    key={lead._id || lead.id}
                    className="border-b border-slate-200/60 hover:bg-white"
                  >
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {lead.created_at
                        ? new Date(lead.created_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {lead.name || "Unknown"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {lead.phone || "-"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {lead.lead_type || lead.intent || "N/A"}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 text-[10px] uppercase tracking-wide">
                        {lead.status || "new"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {lead.source || "CallFlow AI"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
